/** Espace logique du plan (indépendant du viewport — positions en %). */
export const PLAN_LOGICAL_W = 720;
export const PLAN_LOGICAL_H = 480;

export const DRAG_THRESHOLD_PX = 6;
export const MIN_TABLE_SIZE = 56;
export const MAX_TABLE_SIZE = 220;
export const GRID_SNAP = 8;

export const PLAN_SHAPES = ['ROUND', 'SQUARE', 'RECTANGLE'];

export const SIZE_PRESETS = {
  SMALL: 0.82,
  MEDIUM: 1,
  LARGE: 1.28,
};

export function normalizePlanShape(raw) {
  const s = String(raw || 'ROUND').toUpperCase();
  return PLAN_SHAPES.includes(s) ? s : 'ROUND';
}

export function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

export function snapToGrid(value, grid = GRID_SNAP) {
  return Math.round(value / grid) * grid;
}

/**
 * Taille initiale suggérée selon capacité — le commerçant peut toujours ajuster.
 */
export function suggestedDimensionsForCapacity(capacity, shape = 'ROUND') {
  const cap = Math.max(1, Number(capacity) || 4);
  const base = clamp(64 + Math.min(40, cap * 5), MIN_TABLE_SIZE, MAX_TABLE_SIZE);
  const normalized = normalizePlanShape(shape);
  if (normalized === 'RECTANGLE' && cap >= 6) {
    return { width: clamp(base * 1.55, MIN_TABLE_SIZE, MAX_TABLE_SIZE), height: clamp(base * 0.88, MIN_TABLE_SIZE, MAX_TABLE_SIZE) };
  }
  return { width: base, height: base };
}

export function applySizePreset(capacity, shape, presetKey) {
  const factor = SIZE_PRESETS[presetKey] ?? 1;
  const base = suggestedDimensionsForCapacity(capacity, shape);
  return {
    width: clamp(Math.round(base.width * factor), MIN_TABLE_SIZE, MAX_TABLE_SIZE),
    height: clamp(Math.round(base.height * factor), MIN_TABLE_SIZE, MAX_TABLE_SIZE),
  };
}

export function resolveTableLayout(table) {
  const shape = normalizePlanShape(table?.planShape);
  const suggested = suggestedDimensionsForCapacity(table?.capacity, shape);
  let width = table?.planWidth != null ? Number(table.planWidth) : suggested.width;
  let height = table?.planHeight != null ? Number(table.planHeight) : suggested.height;
  if (shape === 'ROUND' || shape === 'SQUARE') {
    const side = Math.max(width, height);
    width = side;
    height = side;
  }
  width = clamp(width, MIN_TABLE_SIZE, MAX_TABLE_SIZE);
  height = clamp(height, MIN_TABLE_SIZE, MAX_TABLE_SIZE);
  const rotation = shape === 'RECTANGLE' && Number(table?.planRotation) === 90 ? 90 : 0;
  const x = table?.planX != null ? Number(table.planX) : 40;
  const y = table?.planY != null ? Number(table.planY) : 40;
  const pos = clampPlanPosition(x, y, width, height);
  return { shape, width, height, rotation, x: pos.x, y: pos.y };
}

export function clampPlanPosition(x, y, width, height) {
  return {
    x: clamp(x, 0, PLAN_LOGICAL_W - width),
    y: clamp(y, 0, PLAN_LOGICAL_H - height),
  };
}

export function logicalToPercent(x, y, width, height) {
  return {
    left: `${(x / PLAN_LOGICAL_W) * 100}%`,
    top: `${(y / PLAN_LOGICAL_H) * 100}%`,
    width: `${(width / PLAN_LOGICAL_W) * 100}%`,
    height: `${(height / PLAN_LOGICAL_H) * 100}%`,
  };
}

export function borderRadiusForShape(shape) {
  const s = normalizePlanShape(shape);
  if (s === 'ROUND') return '9999px';
  if (s === 'SQUARE') return '0.75rem';
  return '0.5rem';
}

/**
 * Détecte si le geste pointer a dépassé le seuil (clic vs drag/resize).
 */
export function pointerExceededThreshold(startX, startY, clientX, clientY, threshold = DRAG_THRESHOLD_PX) {
  const dx = clientX - startX;
  const dy = clientY - startY;
  return Math.hypot(dx, dy) >= threshold;
}

/** Taille utile pour typographie (unités logiques plan). */
export function tableMinDimension(width, height) {
  return Math.min(Number(width) || MIN_TABLE_SIZE, Number(height) || MIN_TABLE_SIZE);
}

export function tableTypographyTier(width, height) {
  const minDim = tableMinDimension(width, height);
  if (minDim < 60) return 'xs';
  if (minDim < 88) return 'sm';
  if (minDim < 120) return 'md';
  return 'lg';
}
